import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { ChartService, ChartConfig } from './chart.service';
import { UsersGridService } from '../../users-grid/users-grid.service';
import { finalize } from 'rxjs/operators';

interface ChartItem {
  id: number;
  label: string;
  value: string;
  icon?: string;
  config?: any;
}

@Component({
    selector: 'app-items-selector',
    templateUrl: './items-selector.component.html',
    styleUrls: ['./items-selector.component.scss'],
    standalone: false
})
export class ItemsSelectorComponent implements OnInit {
  @Output() selectionChanged = new EventEmitter<{selected: ChartItem[]}>();
  
  sourceItems: ChartItem[] = [];
  targetItems: ChartItem[] = [];
  selectedSourceItems: ChartItem[] = [];
  selectedTargetItems: ChartItem[] = [];
  sourceFilter = '';
  targetFilter = '';
  loading = false;
  error: string | null = null;
  saving = false;
  savedSuccessfully = false;
  
  // Map chart types to icons
  private readonly chartIcons: {[key: string]: string} = {
    'advanced-pie': 'pi pi-chart-pie',
    'tree': 'pi pi-sitemap',
    'bar': 'pi pi-chart-bar',
    'line': 'pi pi-chart-line',
    'area': 'pi pi-chart-line',
    'stacked-bar': 'pi pi-chart-bar',
    'stacked-area': 'pi pi-chart-line',
    'pie': 'pi pi-chart-pie',
    'donut': 'pi pi-chart-pie'
  };

  constructor(
    private chartService: ChartService,
    private userService: UsersGridService
  ) {}

  getFilteredSource(): ChartItem[] {
    if (!this.sourceFilter) return this.sourceItems;
    return this.sourceItems.filter(item => 
      item.label.toLowerCase().includes(this.sourceFilter.toLowerCase())
    );
  }

  getFilteredTarget(): ChartItem[] {
    if (!this.targetFilter) return this.targetItems;
    return this.targetItems.filter(item => 
      item.label.toLowerCase().includes(this.targetFilter.toLowerCase())
    );
  }

  isSourceSelected(item: ChartItem): boolean {
    return this.selectedSourceItems.some(i => i.id === item.id);
  }

  isTargetSelected(item: ChartItem): boolean {
    return this.selectedTargetItems.some(i => i.id === item.id);
  }

  toggleSourceSelection(item: ChartItem) {
    const index = this.selectedSourceItems.findIndex(i => i.id === item.id);
    if (index > -1) {
      this.selectedSourceItems.splice(index, 1);
    } else {
      this.selectedSourceItems.push(item);
    }
  }

  toggleTargetSelection(item: ChartItem) {
    const index = this.selectedTargetItems.findIndex(i => i.id === item.id);
    if (index > -1) {
      this.selectedTargetItems.splice(index, 1);
    } else {
      this.selectedTargetItems.push(item);
    }
  }

  moveToTarget() {
    this.targetItems = [...this.targetItems, ...this.selectedSourceItems];
    this.sourceItems = this.sourceItems.filter(item => 
      !this.selectedSourceItems.some(selected => selected.id === item.id)
    );
    this.selectedSourceItems = [];
    this.emitSelectionChange();
  }

  moveAllToTarget() {
    this.targetItems = [...this.targetItems, ...this.sourceItems];
    this.sourceItems = [];
    this.selectedSourceItems = [];
    this.emitSelectionChange();
  }

  moveToSource() {
    this.sourceItems = [...this.sourceItems, ...this.selectedTargetItems];
    this.targetItems = this.targetItems.filter(item => 
      !this.selectedTargetItems.some(selected => selected.id === item.id)
    );
    this.selectedTargetItems = [];
    this.emitSelectionChange();
  }

  moveAllToSource() {
    this.sourceItems = [...this.sourceItems, ...this.targetItems];
    this.targetItems = [];
    this.selectedTargetItems = [];
    this.emitSelectionChange();
  }

  private emitSelectionChange() {
    this.selectionChanged.emit({ selected: this.targetItems });
  }

  ngOnInit() {
    this.loadCharts();
  }

  private loadCharts() {
    this.loading = true;
    this.error = null;
    
    // First load all available charts (uses session-based auth)
    this.chartService.getAvailableCharts().subscribe({
      next: (response) => {
        // Handle null or undefined response
        if (!response || !response.data) {
          console.warn('getAvailableCharts returned null or empty response');
          response = { data: [], message: '', customValues: null };
        }
        
        const availableCharts = (response.data || []).map(chart => ({
          id: chart.id,
          label: chart.name,
          value: chart.type,
          icon: this.chartIcons[chart.type] || 'pi pi-chart-line',
          config: this.parseChartConfig(chart.configs)
        }));
        
        // Then load user's selected charts (uses session-based auth)
        this.chartService.getUserCharts().subscribe({
          next: (userChartsResponse) => {
            // Handle null or undefined response
            if (!userChartsResponse || !userChartsResponse.data) {
              console.warn('getUserCharts returned null or empty response');
              userChartsResponse = { data: [], message: '', customValues: null };
            }
            
            const selectedCharts = (userChartsResponse.data || []).map(chart => ({
              id: chart.id,
              label: chart.name,
              value: chart.type,
              icon: this.chartIcons[chart.type] || 'pi pi-chart-line',
              config: this.parseChartConfig(chart.configs)
            }));
            
            // Filter out charts that are already in selectedCharts from availableCharts
            // This is a workaround for backend API issue where both endpoints return same data
            const selectedIds = new Set(selectedCharts.map(c => c.id));
            const filteredAvailableCharts = availableCharts.filter(chart => !selectedIds.has(chart.id));
            
            this.sourceItems = [...filteredAvailableCharts];
            this.targetItems = [...selectedCharts];
            this.loading = false;
          },
          error: (err) => {
            console.error('getUserCharts error:', err);
            this.handleError('Failed to load user charts', err);
          }
        });
      },
      error: (err) => {
        console.error('getAvailableCharts error:', err);
        this.handleError('Failed to load available charts', err);
      }
    });
  }

  /**
   * Get the current logged-in user ID from the backend
   * This method is no longer used - replaced with direct API call in onSave()
   * @deprecated Use userService.getLoginUser() directly instead
   */
  private getCurrentUserId(): number {
    // This is now handled asynchronously in onSave() method
    throw new Error('Method deprecated - use userService.getLoginUser() instead');
  }

  private parseChartConfig(configString: string): any {
    if (!configString) return null;
    
    try {
      // Try to parse as-is first
      return JSON.parse(configString);
    } catch (e) {
      try {
        // If it fails, try to fix single quotes to double quotes
        const fixedConfig = configString.replace(/'/g, '"');
        return JSON.parse(fixedConfig);
      } catch (e2) {
        console.warn('Failed to parse chart config:', configString, e2);
        return null;
      }
    }
  }

  private handleError(message: string, error: any) {
    console.error(message, error);
    this.error = message;
    this.loading = false;
  }

  onSave() {
    if (this.loading || this.saving) return;
    
    this.saving = true;
    this.savedSuccessfully = false;
    this.error = null;
    
    // First, get the logged-in user ID from the backend
    this.userService.getLoginUser().subscribe({
      next: (userResponse) => {
        if (userResponse && userResponse.rows && userResponse.rows.id) {
          const userId = userResponse.rows.id;
          const chartIds = this.targetItems.map(chart => chart.id);
          
          // Now save the chart selections with the actual user ID
          this.chartService.mapChartsToUser(userId, chartIds)
            .pipe(finalize(() => this.saving = false))
            .subscribe({
              next: (response) => {
                this.savedSuccessfully = true;
                this.emitSelectionChange();
                
                // Reload data from server to reflect actual state
                // This ensures UI matches backend after save
                this.loadCharts();
                
                // Hide success message after 3 seconds
                setTimeout(() => this.savedSuccessfully = false, 3000);
              },
              error: (err) => {
                this.handleError('Failed to save chart selections', err);
              }
            });
        } else {
          this.saving = false;
          this.handleError('Failed to get user information', userResponse);
        }
      },
      error: (err) => {
        this.saving = false;
        this.handleError('Failed to get logged-in user details', err);
      }
    });
  }
}